import { useEffect, useState, useCallback } from 'react';
import { NAVIGATION, SITE_TITLE } from '../constants';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const [activeHash, setActiveHash] = useState('#intro');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHash('#' + entry.target.id);
          }
        });
      },
      { rootMargin: '-10% 0% -80% 0%', threshold: 0 }
    );

    document.querySelectorAll('section[id]').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const handleNavClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      if (href.startsWith('#')) {
        e.preventDefault();
        const el = document.getElementById(href.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          setActiveHash(href);
        }
        onClose();
      }
    },
    [onClose]
  );

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm lg:hidden transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 flex flex-col bg-slate-950 border-r border-slate-900 transition-transform duration-300 lg:static lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Documentation navigation"
      >
        {/* Logo / Home */}
        <div className="p-6 flex items-center h-16 shrink-0">
          <a
            href="#intro"
            onClick={(e) => handleNavClick(e, '#intro')}
            className="flex items-center gap-2 font-bold text-white hover:opacity-80 transition-opacity"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500 shadow-lg shadow-emerald-500/20" aria-hidden="true">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                />
              </svg>
            </div>
            <span>{SITE_TITLE}</span>
          </a>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-6 py-4 space-y-7 custom-scrollbar" aria-label="Table of contents">
          {NAVIGATION.map((section) => (
            <div key={section.title} className="space-y-2">
              <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 px-2">
                {section.title}
              </h2>
              <ul className="space-y-0.5" role="list">
                {section.links.map((link) => {
                  const isLinkActive =
                    activeHash === link.href ||
                    link.children?.some((c) => activeHash === c.href);
                  const isExternal = link.href.startsWith('http');

                  return (
                    <li key={link.title} className="relative">
                      <a
                        href={link.href}
                        onClick={(e) => handleNavClick(e, link.href)}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                        className={`group flex items-center px-2 py-1.5 text-[13px] rounded-md transition-all ${
                          isLinkActive
                            ? 'text-white font-medium'
                            : 'text-slate-400 hover:text-slate-200'
                        }`}
                        aria-current={isLinkActive ? 'location' : undefined}
                      >
                        {link.title}
                        {isExternal && (
                          <svg
                            className="ml-auto w-3 h-3 opacity-40 group-hover:opacity-100 transition-opacity"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                        )}
                      </a>

                      {link.children && (
                        <ul className="mt-0.5 ml-4 border-l border-slate-800/60 space-y-0.5 py-0.5" role="list">
                          {link.children.map((child) => {
                            const isChildActive = activeHash === child.href;
                            return (
                              <li key={child.title}>
                                <a
                                  href={child.href}
                                  onClick={(e) => handleNavClick(e, child.href)}
                                  className={`flex items-center pl-4 pr-3 py-1 text-[12px] transition-colors border-l -ml-px ${
                                    isChildActive
                                      ? 'text-emerald-400 border-emerald-500 font-medium'
                                      : 'text-slate-500 hover:text-slate-300 border-transparent hover:border-slate-700'
                                  }`}
                                  aria-current={isChildActive ? 'location' : undefined}
                                >
                                  {child.title}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}
